import { createClient } from "@supabase/supabase-js";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
   throw new Error(
      "Missing Supabase environment variables. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env file."
   );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export const shouldRefreshSession = (session) => {
   if (!session || !session.expires_at) return false;

   const now = Math.floor(Date.now() / 1000);
   const timeUntilExpiry = session.expires_at - now;

   return timeUntilExpiry < 300;
};

// Utility function to handle automatic session refresh
export const refreshSessionIfNeeded = async () => {
   try {
      const {
         data: { session },
      } = await supabase.auth.getSession();

      if (session && shouldRefreshSession(session)) {
         const { data, error } = await supabase.auth.refreshSession();
         if (error) throw error;
         return data;
      }

      return { session };
   } catch (error) {
      throw error;
   }
};

// Helper functions for authentication
export const authHelpers = {
   // Get current user
   getCurrentUser: async () => {
      const {
         data: { user },
         error,
      } = await supabase.auth.getUser();
      if (error) throw error;
      return user;
   },

   // Get current session
   getCurrentSession: async () => {
      const {
         data: { session },
         error,
      } = await supabase.auth.getSession();
      if (error) throw error;
      return session;
   },

   refreshSession: async () => {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      return data;
   },

   // Sign up with phone and password
   signUp: async (phone, password, userData = {}) => {
      const { data, error } = await supabase.auth.signUp({
         phone,
         password,
         options: {
            data: userData,
         },
      });
      if (error) throw error;
      return data;
   },

   // Sign in with phone and password
   signInWithPassword: async (phone, password) => {
      const { data, error } = await supabase.auth.signInWithPassword({
         phone,
         password,
      });
      if (error) throw error;
      return data;
   },

   // Sign in with OTP
   signInWithOtp: async (phone) => {
      const { data, error } = await supabase.auth.signInWithOtp({
         phone,
      });
      if (error) throw error;
      return data;
   },

   // Verify OTP
   verifyOtp: async (phone, token, type = "sms") => {
      const { data, error } = await supabase.auth.verifyOtp({
         phone,
         token,
         type,
      });
      if (error) throw error;
      return data;
   },

   // Sign out
   signOut: async () => {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { success: true };
   },

   // Listen to auth state changes
   onAuthStateChange: (callback) => {
      return supabase.auth.onAuthStateChange(callback);
   },
};

export default supabase;
